import { FC, Reducer, useEffect, useLayoutEffect, useMemo, useReducer } from 'react';
import { Alert } from '../../../../../shared/lib/common/Alert';
import { GRAPHQL_QUERY_POLICY } from '../../../../../shared/lib/constants';
import { useAuthContext } from '../../../../../shared/lib/contexts/AuthContext';
import { ChildrenType } from '../../../../../shared/lib/interfaceTypes';
import { Auth_Action } from '../../../../../shared/lib/reducers/AuthContextReducer';
import { User, useFetchAllContactsLazyQuery, useGetSubscriptionDetailLazyQuery, useGetUserLazyQuery } from '../../../generated';
import { AUTH_TOKEN } from '../../constants';
import { AppContextReducerState } from '../../interfaceTypes';
import { ActionType, AppAction, initialState, reducer } from '../../reducer/AppContextReducer';
import { AppContext } from './index';
import { AppLoader } from '../../../../../shared/lib/common/AppLoader';

/**
 * AuthContextProvider is a component that wraps its children with the AuthContext provider,
 * enabling access to authentication state and actions throughout the component tree.
 *
 * @component
 * @prop {React.ReactNode}  children - The child components that should be wrapped by the provider.
 * @returns {JSX.Element} The JSX element wrapping the children with the AuthContext provider.
 */
export const AppProvider: FC<ChildrenType> = ({ children }): JSX.Element => {
  const { setIsLoggedIn, setLoggedInUser, isLoggedIn, loggedInUser, userLoader, dispatchAuthAction, setUserLoader } = useAuthContext();
  const hasToken = sessionStorage.getItem(AUTH_TOKEN) || localStorage.getItem(AUTH_TOKEN);
  const [state, dispatch] = useReducer<Reducer<AppContextReducerState, AppAction>>(reducer, initialState);
  const store = useMemo(() => ({ ...state, dispatch }), [state]);
  const { currentUser, cardsData } = store;
  const { isSubscribed } = currentUser ?? {};
  const { customerStripeId } = isSubscribed ?? {};

  const [getContacts] = useFetchAllContactsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { fetchAllContacts } = data ?? {};

      if (fetchAllContacts) {
        dispatch({ type: ActionType.SET_CARDS_DATA, cardsData: fetchAllContacts });
      }
    }
  });

  const [getSubscriptionDetail] = useGetSubscriptionDetailLazyQuery({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { getSubscriptionDetail } = data;

      if (getSubscriptionDetail) {
        const { data: getSubscriptionDetailData } = getSubscriptionDetail ?? {};
        const { items } = getSubscriptionDetailData[0] ?? {};
        const { data: itemsData } = items ?? {};
        const { plan } = itemsData.slice(-1)[0] ?? {};
        dispatch({ type: ActionType.SET_SELECTED_PLAN, selectedPlan: plan });
      }
    }
  });

  useEffect(() => {
    customerStripeId && (async () => await getContacts())();
  }, []);

  useEffect(() => {
    if (customerStripeId) {
      getSubscriptionDetail({
        variables: { customerStripeId }
      });
    }
  }, [customerStripeId, getSubscriptionDetail]);

  store.isAppUserLoggedIn = isLoggedIn;
  store.currentUser = loggedInUser as User;
  store.appUserLoader = userLoader;

  const setIsAppUserLoggedIn = (isAppUserLoggedIn: boolean) => {
    setIsLoggedIn(isAppUserLoggedIn);
  };

  const setCurrentUser = (currentUser: User | null) => {
    setLoggedInUser(currentUser);
  };

  const [fetchUser] = useGetUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      setCurrentUser(null);
      dispatchAuthAction({ type: Auth_Action.SET_USER_LOADER, userLoader: false });
    },

    onCompleted(data) {
      if (data) {
        setUserLoader(false);
        dispatchAuthAction({ type: Auth_Action.SET_USER_LOADER, userLoader: true });
        const { me } = data;

        if (me) {
          setCurrentUser(me);
          dispatchAuthAction({ type: Auth_Action.SET_USER_LOADER, userLoader: false });
        }
      }
    }
  });

  useLayoutEffect(() => {
    setIsAppUserLoggedIn(!!hasToken);

    if (isLoggedIn && hasToken && !currentUser) {
      setUserLoader(true);
      (async () => await fetchUser())();
    } else if (customerStripeId && hasToken && cardsData.length === 0) {
      (async () => await getContacts())();
    }
  }, [isLoggedIn, hasToken]);

  if (userLoader && !currentUser) return <AppLoader />;

  return <AppContext.Provider value={{ ...store, setIsAppUserLoggedIn, setCurrentUser }}>{children}</AppContext.Provider>;
};
